import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    inject,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { FiInfiniteScrollDataSource, TSortOrder } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { IOnboardingReportOnboarding } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import { MatSortDirection, ObjectTypes } from '@fi-app/shared/types';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiOnboardingStatusesConfig } from '@fi-app/fi-onboarding/fi-onboarding-consts';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { FiOnboardingReportHelperService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-helper.service';

@Component({
    selector: 'fi-total-onboardings-table',
    templateUrl: './fi-total-onboardings-table.component.html',
    styleUrl: './fi-total-onboardings-table.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-total-onboardings-table' },
    standalone: false,
})
export class FiTotalOnboardingsTableComponent
    extends FiInfiniteScrollDataSource<IOnboardingReportOnboarding>
    implements OnChanges
{
    @Input({ required: true }) onboardings: IOnboardingReportOnboarding[];
    @Input({ required: true }) widgetId: number;

    public readonly itemsListType = FiItemsListType.OnboardingReportCompletion;
    public readonly objectType = ObjectTypes.OnboardingReport;
    public readonly defaultSortColumnName = 'Status';

    protected readonly FiOnboardingStatusesConfig = FiOnboardingStatusesConfig;

    private readonly fiOnboardingHelperService = inject(FiOnboardingHelperService);
    private readonly fiCustomerDataService = inject(FiCustomerDataService);
    private readonly fiStateService = inject(FiStateService);
    private readonly fiOnboardingReportHelperService = inject(FiOnboardingReportHelperService);

    protected readonly isSyncOnboardingEnabled: boolean = this.fiCustomerDataService.isSynchronizedOnboardingEnabled();
    protected getRowClassFn = (item: IOnboardingReportOnboarding): string => 'cursor-pointer';

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.onboardings;
        if (change) {
            this.reloadData();
        }
    }

    public getListData(): Promise<IOnboardingReportOnboarding[]> {
        return Promise.resolve(this.onboardings);
    }

    protected getSortOrderFn(): TSortOrder<IOnboardingReportOnboarding> {
        return this.fiOnboardingReportHelperService.getStatusSortOrderFn(
            this.selectedSort.active,
            this.isDescendingSort(),
            super.getSortOrderFn(),
        );
    }

    protected getCategoryText(item: IOnboardingReportOnboarding): string {
        return this.fiOnboardingHelperService.getCategoryText(item.Category);
    }

    protected getBusinessUnitsCount(item: IOnboardingReportOnboarding): number {
        return item.BusinessUnits.length;
    }

    protected isAvailable(item: IOnboardingReportOnboarding) {
        return item.Status !== FiOnboardingStatusType.NotAvailable;
    }

    protected onRowClick(item: IOnboardingReportOnboarding) {
        this.fiStateService.go(FiStateNames.Onboarding.Report.Widget.Locations._name, {
            id: this.widgetId,
            onboardingId: item.Id,
        });
    }

    protected readonly MatSortDirection = MatSortDirection;
}
